import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useSiteData } from '../context/SiteDataContext';
import { Home, Truck, Sparkles, Square, Building2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const iconMap = {
  Home,
  Truck,
  Sparkles,
  Square,
  Building2,
  Stairs: Building2,
};

const ServicesSection = ({ showAll = false }) => {
  const { language, t } = useLanguage();
  const { services } = useSiteData();
  const serviceList = services[language];
  const displayedServices = showAll ? serviceList : serviceList.slice(0, 4);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Sparkles size={16} />
            {language === 'sv' ? 'Våra Tjänster' : 'Our Services'}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t.services.title}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {t.services.subtitle}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedServices.map((service, index) => {
            const Icon = iconMap[service.icon] || Sparkles;
            return (
              <div
                key={service.id}
                className="group bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-xl hover:border-purple-200 transition-all duration-300 hover:-translate-y-1"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg shadow-purple-500/20">
                  <Icon className="text-white" size={24} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {service.description}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <span className="text-purple-600 font-semibold">
                    {language === 'sv' ? 'från' : 'from'} {service.basePrice} kr
                    <span className="text-gray-500 font-normal text-sm ml-1">
                      {service.priceUnit}
                    </span>
                  </span>
                  <Link
                    to={`/services/${service.slug || service.id}`}
                    className="text-purple-600 hover:text-purple-700 font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all"
                  >
                    {t.services.learnMore}
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            );
          })};
        </div>

        {/* View All Button */}
        {!showAll && (
          <div className="text-center mt-12">
            <Link to="/services">
              <Button variant="outline" className="border-2 border-purple-600 text-purple-600 hover:bg-purple-50 px-8 py-6 text-lg">
                {language === 'sv' ? 'Se alla tjänster' : 'View all services'}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicesSection;
